import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';
import { getDeviceInfo } from '../utils/deviceUtils';
import { Button } from '@mui/material';
import CustomSnackbar from '../components/ui/Snackbar';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        // Don't clear error here - let it persist until explicitly cleared
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  
  // Inactivity detection state
  const [lastActivity, setLastActivity] = useState(Date.now()); // eslint-disable-line no-unused-vars
  const [warningShown, setWarningShown] = useState(false);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  
  // Snackbar state for inactivity warnings
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
    action: null
  });

  // Check for existing token on app load
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        try {
          // Use stored user data (token validation will happen on first API call)
          const userData = JSON.parse(user);
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: userData,
              token,
            },
          });
        } catch (error) {
          // Invalid stored data, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await authAPI.login({ ...credentials, ...getDeviceInfo() });

      if (response.data.mfaRequired) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return {
          success: true,
          mfaRequired: true,
          nextStep: response.data.nextStep,
          mfaToken: response.data.token,
        };
      }

      const { user, token } = response.data;

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token },
      });

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const loginSuccess = (user, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: { user, token } });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Enhanced logout function that handles desk sessions
  const logoutWithDeskSessionCleanup = useCallback(async () => {
    try {
      // Call the enhanced logout API that handles desk session cleanup
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      // Clear inactivity timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      
      // Close any open snackbars
      setSnackbar(prev => ({ ...prev, open: false }));
    }
  }, []);

  // Track user activity (only for desk agents)
  const trackActivity = useCallback(() => {
    // Only track activity for desk agents
    if (state.user?.role !== 'AGENT') return;
    
    setLastActivity(Date.now());
    setWarningShown(false);
    
    // Clear existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Set new inactivity timer (5 minutes)
    inactivityTimerRef.current = setTimeout(() => {
      // Show inactivity warning directly (inline logic)
      if (warningShown || state.user?.role !== 'AGENT') return;
      
      setWarningShown(true);
      setSnackbar({
        open: true,
        message: "La session expirera dans 5 minutes en raison de l'inactivité",
        severity: "warning",
        action: (
          <Button 
            onClick={() => {
              setLastActivity(Date.now());
              setWarningShown(false);
              if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
              if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
              setSnackbar(prev => ({ ...prev, open: false }));
              setSnackbar({
                open: true,
                message: "Session prolongée",
                severity: "success"
              });
              trackActivity(); // Restart timer
            }}
            color="inherit"
            size="small"
          >
            Rester connecté
          </Button>
        )
      });
      
      // Set final logout timer (5 more minutes)
      warningTimerRef.current = setTimeout(() => {
        logoutWithDeskSessionCleanup();
      }, 300000); // 5 minutes after warning
    }, 300000); // 5 minutes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.role, logoutWithDeskSessionCleanup]);


  // Check if user has specific role
  const hasRole = (role) => {
    return state.user?.role === role;
  };

  // Check if user has access to specific caisse
  const hasCaisseAccess = (caisseId) => {
    if (!state.user) return false;
    if (state.user.role === 'MANAGER') return true; // Managers have access to all caisses
    return state.user.assignedCaisses?.some(caisse => caisse.id === caisseId);
  };

  // Set up activity listeners (only for desk agents)
  useEffect(() => {
    if (!state.isAuthenticated || state.user?.role !== 'AGENT') {
      // Clear timers if not a desk agent
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      return;
    }
    
    // Listen for user activity
    document.addEventListener('click', trackActivity);
    document.addEventListener('keypress', trackActivity);
    document.addEventListener('mousemove', trackActivity);
    document.addEventListener('scroll', trackActivity);
    
    // Initial timer setup
    trackActivity();
    
    return () => {
      document.removeEventListener('click', trackActivity);
      document.removeEventListener('keypress', trackActivity);
      document.removeEventListener('mousemove', trackActivity);
      document.removeEventListener('scroll', trackActivity);
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [state.isAuthenticated, state.user?.role, trackActivity]);

  const value = {
    ...state,
    login,
    loginSuccess,
    logout: logoutWithDeskSessionCleanup,
    clearError,
    hasRole,
    hasCaisseAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Inactivity warning snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        action={snackbar.action}
        autoHideDuration={snackbar.severity === 'success' ? 3000 : null}
      />
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

